import React, { useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { Card, CardBody, CardHeader, Col, Row } from 'reactstrap';
import SimpleBar from 'simplebar-react';
import { formatDate } from '../../../../application/shared/FormatedDate';
import { Project } from '../../../../domain/projects/project';
import { ContactListByProjectDTO } from '../../../../infrastructure/http/dtos/customer-contacts/contactListByProjectDTO';
import { ContactRepository } from '../../../../infrastructure/repositories/customer-contacts/contactRepository';
import { ProjectsRepository } from '../../../../infrastructure/repositories/projects/projectRepository';
import ProjectMilestoneListTable from '../tables/ProjectMilestoneListTable';
import Swal from 'sweetalert2';
import { ProjectFilesListDTO } from '../../../../infrastructure/http/dtos/projects/projectFilesListDTO';

interface OverviewTabProps {
    project: Project | undefined;
}

const OverviewTab = ({ project }: OverviewTabProps) => {
    const { id } = useParams<{ id: string }>();
    const [customerContacts, setCustomerContacts] = useState<
        ContactListByProjectDTO[]
    >([]);
    const [selectedFile, setSelectedFile] = useState<FormData | null>(null);
    const [projectFilesList, setProjectFilesList] = useState<ProjectFilesListDTO[]>(
        []
    );
    const [projectChange, setProjectChange] = useState<Project>();

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        const file = event.target.files?.[0];
        const formData = new FormData();
        formData.append('file', file || '');

        setSelectedFile(formData);
    };

    const handleFileUpload = async () => {
        if (selectedFile) {
            const projectId = id;

            try {
                await ProjectsRepository.UploadProjectFile(projectId, selectedFile);
                Swal.fire('Archivo cargado exitosamente!', '', 'success');
                const updatedProjectFilesList =
                    await ProjectsRepository.GetProjectsFilesList(id ?? '');
                setProjectFilesList(updatedProjectFilesList);
                setSelectedFile(null);
            } catch (error) {
                Swal.fire('Error al cargar el archivo', '', 'error');
            }
        }
    };

    const updateProjectPercentage = async () => {
        if (id) {
            try {
                const projectResponse = await ProjectsRepository.GetProject(id);
                setProjectChange(projectResponse);
            } catch (error) {
                console.error('Error al obtener el proyecto:', error);
            }
        }
    };

    useEffect(() => {
        if (id) {
            ContactRepository.GetCustomerContactByProjectListSelector(id).then(
                (response) => {
                    setCustomerContacts(response);
                }
            );
        }
    }, [id]);

    useEffect(() => {
        if (id) {
            ProjectsRepository.GetProjectsFilesList(id).then((response) => {
                setProjectFilesList(response);
            });
        }
    }, [id]);

    const handleDownloadClick = (url: string, fileName: string) => {
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    return (
        <React.Fragment>
            <Row>
                <Col xl={9} lg={8}>
                    <Card>
                        <CardBody>
                            <div className='text-muted'>
                                <h6 className='mb-3 text-uppercase'>
                                    Sobre el proyecto
                                </h6>
                                <p>{project?.summary}</p>

                                <div className='pt-3 border-top border-top-dashed mt-4'>
                                    <Row>
                                        <Col lg={3} sm={6}>
                                            <div>
                                                <p className='mb-2 text-uppercase fw-medium'>
                                                    Fecha Inicial :
                                                </p>
                                                <h5 className='fs-14 mb-0'>
                                                    {project?.startDate
                                                        ? formatDate(
                                                              project.startDate
                                                          )
                                                        : ''}
                                                </h5>
                                            </div>
                                        </Col>
                                        <Col lg={3} sm={6}>
                                            <div>
                                                <p className='mb-2 text-uppercase fw-medium'>
                                                    Fecha Final :
                                                </p>
                                                <h5 className='fs-14 mb-0'>
                                                    {project?.endDate
                                                        ? formatDate(project.endDate)
                                                        : ''}
                                                </h5>
                                            </div>
                                        </Col>
                                        <Col lg={3} sm={6}>
                                            <div>
                                                <p className='mb-2 text-uppercase fw-medium'>
                                                    Avance :
                                                </p>
                                                <div className='badge bg-success fs-11'>
                                                    {projectChange?.percentage ||
                                                        project?.percentage}
                                                    %
                                                </div>
                                            </div>
                                        </Col>
                                    </Row>
                                </div>
                            </div>
                        </CardBody>
                    </Card>
                    <Card>
                        <CardHeader className='align-items-center d-flex'>
                            <h4 className='card-title mb-0 flex-grow-1'>
                                Gestión de Hitos
                            </h4>
                        </CardHeader>

                        <CardBody className='p-0'>
                            <SimpleBar
                                style={{ height: '400px' }}
                                className='px-3 mx-n3 mb-2'
                            >
                                <ProjectMilestoneListTable
                                    updateProjectPercentage={updateProjectPercentage}
                                />
                            </SimpleBar>
                        </CardBody>
                    </Card>
                </Col>

                <Col xl={3} lg={4}>
                    <Card>
                        <CardHeader className='align-items-center d-flex border-bottom-dashed'>
                            <h4 className='card-title mb-0 flex-grow-1'>
                                Contactos
                            </h4>
                        </CardHeader>

                        <CardBody>
                            <SimpleBar
                                data-simplebar
                                style={{ height: '210px' }}
                                className='mx-n3 px-3'
                            >
                                <div className='vstack gap-3'>
                                    {customerContacts.map((contact) => (
                                        <div
                                            key={contact.id}
                                            className='d-flex align-items-center'
                                        >
                                            <div className='avatar-sm mx-2'>
                                                <div className='avatar-title bg-light text-primary rounded fs-24'>
                                                    <i className='ri-shield-user-line'></i>
                                                </div>
                                            </div>
                                            <div className='d-flex flex-column justify-content-center mt-2'>
                                                <h5 className='fs-13 mb-0'>
                                                    {contact.name}
                                                </h5>
                                                <div className='d-flex align-items-center'>
                                                    <p className='text-muted fs-11'>
                                                        {contact.position}
                                                    </p>
                                                    <p className='fs-13 ms-2'>
                                                        <i className='ri-phone-line'></i>
                                                        {contact.cellphoneNumber}{' '}
                                                    </p>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </SimpleBar>
                        </CardBody>
                    </Card>
                    <Card>
                        <CardHeader className='d-flex flex-wrap border-bottom-dashed justify-content-center align-items-center p-2'>
                            <div className='flex-shrink-0 mt-2 d-flex align-items-center'>
                                <h4 className='card-title mb-0'>Importar Archivo</h4>
                            </div>
                            <div className='flex-shrink-0 mt-1 mx-2'>
                                <label
                                    htmlFor='fileInput'
                                    className='btn btn-soft-info btn-sm mt-2'
                                >
                                    <i className='ri-upload-2-fill me-1 align-bottom'></i>{' '}
                                    <input
                                        type='file'
                                        id='fileInput'
                                        onChange={handleFileChange}
                                        style={{ display: 'none' }}
                                    />
                                    Seleccione un archivo
                                </label>
                            </div>
                            <div className='flex-shrink-0 mb-0'>
                                <button
                                    type='button'
                                    className='btn btn-primary btn-sm'
                                    onClick={handleFileUpload}
                                    disabled={!selectedFile}
                                >
                                    <i className='ri-send-plane-fill'></i>
                                </button>
                            </div>
                        </CardHeader>

                        <CardBody>
                            <SimpleBar
                                data-simplebar
                                style={{ height: '235px' }}
                                className='mx-n3 px-3'
                            >
                                <div className='vstack gap-2'>
                                    {projectFilesList.map((projectFile) => (
                                        <div
                                            key={projectFile.id}
                                            className='border rounded border-dashed p-2'
                                            title={projectFile.name}
                                        >
                                            <div className='d-flex align-items-center'>
                                                <div className='flex-shrink-0 me-3'>
                                                    <div className='avatar-sm '>
                                                        <div className='avatar-title bg-light text-secondary rounded fs-23'>
                                                            <div className='d-flex'>
                                                                <i className='ri-folder-zip-line'></i>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                                <div className='flex-grow-1 overflow-hidden'>
                                                    <h5 className='fs-13 mb-1'>
                                                        <span className='text-body text-truncate d-block'>
                                                            {projectFile.name}
                                                        </span>
                                                    </h5>
                                                    <div>
                                                        {projectFile.containerName}
                                                    </div>
                                                </div>
                                                <div className='flex-shrink-0 ms-2'>
                                                    <div className='d-flex gap-1'>
                                                        <button
                                                            type='button'
                                                            className='btn btn-light text-muted btn-sm fs-15'
                                                            onClick={() =>
                                                                handleDownloadClick(
                                                                    projectFile.url,
                                                                    projectFile.name
                                                                )
                                                            }
                                                        >
                                                            <i className='ri-download-2-line'></i>
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            </SimpleBar>
                        </CardBody>
                    </Card>
                </Col>
            </Row>
        </React.Fragment>
    );
};

export default OverviewTab;
